import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { listApps, AppSummary } from '../services/api';

const SearchIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
    <circle cx="9" cy="9" r="6" />
    <path d="M17 17l-4-4" strokeLinecap="round" />
  </svg>
);

const DocIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5">
    <path
      d="M6 3h6l3 3v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
      strokeLinejoin="round"
    />
    <path d="M12 3v3h3" strokeLinejoin="round" />
  </svg>
);

const AppCardSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-neutral-200 bg-white p-5">
    <div className="mb-3 h-9 w-9 rounded-lg bg-neutral-200" />
    <div className="h-4 w-2/3 rounded bg-neutral-200" />
    <div className="mt-2 h-3 w-full rounded bg-neutral-200" />
    <div className="mt-4 h-3 w-1/3 rounded bg-neutral-200" />
  </div>
);

const AppList = () => {
  const [apps, setApps] = useState<AppSummary[]>([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listApps()
      .then(setApps)
      .catch(() => setError('Failed to load apps'))
      .finally(() => setIsLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(
      (app) =>
        app.name.toLowerCase().includes(q) ||
        (app.description || '').toLowerCase().includes(q)
    );
  }, [apps, query]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />

      <main className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold tracking-tight text-neutral-900">App documentation</h2>
          <p className="mt-1 text-sm text-neutral-500">
            In-depth docs for every duo app — architecture, data model, API endpoints and deployment.
          </p>
        </div>

        <div className="relative mb-8 max-w-md">
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-neutral-400">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search apps..."
            className="w-full pl-9"
          />
        </div>

        {error && <p className="text-red-600">{error}</p>}

        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <AppCardSkeleton key={i} />
            ))}
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-neutral-300 py-16 text-center text-sm text-neutral-500">
            {apps.length === 0 ? 'No documentation has been published yet.' : 'No apps match your search.'}
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((app) => (
              <Link
                key={app.slug}
                to={`/${app.slug}`}
                className="group block rounded-xl border border-neutral-200 bg-white p-5 no-underline transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-soft"
              >
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
                  <DocIcon />
                </div>
                <h3 className="font-medium text-neutral-900 group-hover:text-brand-700">{app.name}</h3>
                {app.description && <p className="mt-1 text-sm text-neutral-500">{app.description}</p>}
                <p className="mt-3 text-xs text-neutral-400">
                  Updated {new Date(app.updated_at).toLocaleDateString()}
                </p>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AppList;
